import { Localized, useLocalization } from "@fluent/react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { useEffect, useState } from "react"
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import { FlexDiv } from "../../app/Utils"
import { LoadCompanies } from "../../app/AppDataLoader"
import { ButtonGroup, Skeleton } from "@mui/material"
import { selectCompanyById } from "../company/companiesSlice"
import { store } from "../../app/store"
import { UserStatusLabel } from "./UserStatusLabel"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../settings/settingsSlice"
import { useNavigate } from "react-router-dom"
import { LoadingContainer } from "../../app/LoadingContainer"
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar"
import { AppUser, loadAppUsers } from "./administration"
import { unwrapResult } from "@reduxjs/toolkit"
import { AddFabButton } from "../../app/components/AddFabButton"
import { CompanyRole, loadCompanyRoles } from "../administration/companyRoles/companyRolesApi"

export const CompanyUsers = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const [allUsers, setAllUsers] = useState<AppUser[] | undefined>(undefined)
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [companyRoles, setCompanyRoles] = useState<CompanyRole[]>([])

    useEffect(() => { 
        console.log('jol')
        setIsLoading(true);
        dispatch(loadAppUsers())
        .then(unwrapResult)
        .then(x => setAllUsers(x))
        .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => {
        dispatch(loadCompanyRoles()).then(unwrapResult)
            .then(setCompanyRoles)
            .catch((error) => console.error(error))
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleGoToClick = (id) => {
        navigate(`/company/user/edit/${id}`)
    };

    const handleOpenCreateForm = () => {
        navigate(`/company/user/create`)
    };

    const columns: GridColDef<AppUser, any, any>[] = [
        {
            field: 'userName',
            headerName: l10n.getString("username", null, "username"),
            width: 300
        },
        {
            field: 'firstName',
            headerName: l10n.getString("user-first-name", null, "Imię"),
            flex: 1,
        },
        {
            field: 'surname',
            headerName: l10n.getString("user-last-name", null, "Nazwisko"),
            flex: 1,
        },
        {
            field: 'companyId',
            headerName: l10n.getString("user-company", null, "Firma"),
            flex: 1,
            valueGetter: (_, row) => {
                const entity = selectCompanyById(store.getState(), row.companyId)

                if (!entity) {
                    return ""
                } else {
                    return entity.shortName
                }
            }
        },
        {
            // Same single role as the edit form: admin, or the plain user's company role.
            field: 'role',
            headerName: l10n.getString("user-role", { num: 0 }, "Rola"),
            width: 220,
            valueGetter: (_, row) => {
                if (row.role === "admin") {
                    return l10n.getString("user-role-admin", null, "Administrator")
                }
                if (row.role === "user") {
                    const companyRole = companyRoles.find(r => r.id === row.companyRoleId)
                        ?? (row.companyRoleId ? undefined : companyRoles.find(r => r.isDefault))
                    return companyRole?.name ?? ""
                }
                return row.role ?? ""
            }
        },
        {
            field: 'position',
            headerName: l10n.getString("user-position", null, "Stanowisko"),
            width: 300
        },
        {
            field: 'status',
            headerName: l10n.getString("user-status", null, "Status"),
            width: 200,

            valueGetter: (_, row) => {
                var status = row.status.toUpperCase();
                switch (status) {
                    case "LOCKED":
                        return l10n.getString("user-status-locked", null, "")
                    case "NOT_CONFIRMED":
                        return l10n.getString("user-status-not-confirmed", null, "")
                    case "ACTIVE":
                        return l10n.getString("user-status-active", null, "")
                    default:
                        return ''
                }
            },
            renderCell: (params) => <UserStatusLabel status={params.row.status} />
        },
        {
            field: 'actions', type: 'actions', width: 150,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 75 }}
                            onClick={() => handleGoToClick(params.row.id)}
                            color="secondary"
                            variant="outlined" >
                            <Localized id="edit">
                                <span>Edytuj</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]
    
    if (isLoading) {
        return <>
            <LoadingContainer/>
        </>
    }

    return (        
        <LoadCompanies component={<Skeleton variant="rectangular" />}>
            <TableContainer component={Paper}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <DataGrid localeText={getCustomLocaleText()}
                        style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                        rows={allUsers ?? []}
                        columns={columns}
                        initialState={{
                            sorting: { sortModel: [{ field: 'userName', sort: 'asc' }] }
                        }}
                        onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                        slots={{ toolbar: QuickSearchToolbar }}
                    />
                </div>

                <FlexDiv>
                    <AddFabButton
                        onClick={handleOpenCreateForm}
                    />
                </FlexDiv>
            </TableContainer>
        </LoadCompanies>        
    );
}
